import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Progress, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AlertOutlined,
  ArrowRightOutlined,
  BarChartOutlined,
  DashboardOutlined,
  FlagOutlined,
  LineChartOutlined,
  PieChartOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
  CalendarOutlined,
  CheckOutlined,
  CheckSquareOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
  UndoOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import GridLayout, { Layout, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatFinanceCurrency as formatCurrency, pendingInvoiceCostLabel } from '../modules/reports/pendingInvoiceCost';
import { formatLocalizedPercent, formatLocalizedQuantity } from '../utils/numberFormatting';
import { authService } from '../services/auth';
import dayjs from 'dayjs';
import { DashboardDataDto } from '../dto/report';
import { OrderDto } from '../dto/order';
import { fetchDashboardData as fetchDashboardDataFromApi } from '../modules/dashboard/dashboardApi';
import { readDashboardLayout, writeDashboardLayout } from '../modules/dashboard/dashboardLayoutStorage';
import {
  DASHBOARD_GRID_COLUMNS,
  DashboardWidgetId,
  DashboardAccess,
  DashboardWidgetLayoutItem,
  getAvailableDashboardWidgets,
  getDashboardWidgetDefinition,
  getDefaultDashboardLayout,
  isDefaultDashboardLayout,
  resolveDashboardLayout,
} from '../modules/dashboard/dashboardWidgets';
import DashboardAddWidgetModal from '../modules/dashboard/DashboardAddWidgetModal';
import {
  ExpectedDeliveriesWidget,
  MyTasksWidget,
  UpcomingTransportsWidget,
} from '../modules/dashboard/DashboardLiveWidgets';
import { isTesterMode } from '../modules/tester/testerModeAccessPolicy';
import { canReadTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { canReadInternalTasks } from '../modules/tasks/internalTaskAccessPolicy';
import {
  DashboardBucketViewModel,
  buildDashboardBucketLink,
  buildDashboardBucketViewModels,
  buildDashboardDeadlineBucket,
  buildDashboardStatusCards,
} from '../modules/dashboard/dashboardWorkflow';
import CustomerPaymentsPanel from '../modules/dashboard/CustomerPaymentsPanel';
import DashboardWidgetHeader from '../modules/dashboard/DashboardWidgetHeader';
import { ObjectCardTile, ObjectCardTone } from '../components/layout/ObjectCard';
import { ORDER_STATUS_COLORS } from '../utils/orderStatus';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import { getOrderMarginCostState } from '../modules/orders/orderMarginSummary';
import { useFeedback } from '../utils/feedback';
import '../components/layout/AppPage.css';
import './Dashboard.css';

const { Text } = Typography;

// Count rows keep their meaning in the tile colour; a zero is always grey.
const COUNT_TONE_TILES: Record<string, ObjectCardTone> = {
  danger: 'pink',
  warning: 'orange',
  primary: 'blue',
  neutral: 'indigo',
};

// Space between widgets: the 16 px gap between stacked cards (DESIGN.md §4).
const GRID_GAP_PX = 16;

const ResponsiveGridLayout = WidthProvider(GridLayout);

// Below this width the panel is a single column and layout editing is off.
const NARROW_BREAKPOINT_PX = 900;
const GRID_ROW_HEIGHT_PX = 44;

const BUCKET_ICONS: Record<string, React.ReactNode> = {
  alert: <AlertOutlined />,
  calendar: <CalendarOutlined />,
  clock: <ClockCircleOutlined />,
  fileDone: <FileDoneOutlined />,
  truck: <TruckOutlined />,
};

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation(['dashboard', 'common', 'orders']);
  const location = useLocation();
  const navigate = useNavigate();
  const feedback = useFeedback();
  const accessDeniedState = Boolean((location.state as { accessDenied?: boolean } | null)?.accessDenied);
  const [accessDenied, setAccessDenied] = useState(accessDeniedState);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [data, setData] = useState<DashboardDataDto | null>(null);
  const [storedLayout, setStoredLayout] = useState<unknown>(() => readDashboardLayout(authService.getCurrentUser()?.id));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DashboardWidgetLayoutItem[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [narrow, setNarrow] = useState(() => window.innerWidth < NARROW_BREAKPOINT_PX);
  const user = authService.getCurrentUser();
  const isTester = isTesterMode(user);
  const canViewFullKpis = authService.canViewDashboardFullKpis();
  // Each capability mirrors the rule of the endpoint its widget reads.
  const canOpenTransports = canReadTransportRoutes(user);
  const canManageSupplierOrders = authService.canManageSupplierOrders();
  const canUseTasks = canReadInternalTasks(user);
  const dashboardAccess = useMemo<DashboardAccess>(() => ({
    fullKpis: canViewFullKpis,
    transports: canOpenTransports,
    supplierOrders: canManageSupplierOrders,
    tasks: canUseTasks,
  }), [canViewFullKpis, canOpenTransports, canManageSupplierOrders, canUseTasks]);
  const availableWidgets = useMemo(() => getAvailableDashboardWidgets(dashboardAccess), [dashboardAccess]);
  const pinnedWidgets = useMemo(
    () => resolveDashboardLayout(storedLayout, dashboardAccess),
    [storedLayout, dashboardAccess],
  );
  // Live widgets refetch when the panel refreshes.
  const refreshTick = lastRefreshedAt?.getTime() ?? 0;
  // While editing, the panel shows the draft; otherwise the saved layout.
  const shownLayout = editing ? draft : pinnedWidgets;

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < NARROW_BREAKPOINT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Editing needs a wide grid. Narrowing the window mid-edit drops the draft
  // rather than keeping a mode where drags look applied but are never saved.
  useEffect(() => {
    if (narrow && editing) setEditing(false);
  }, [narrow, editing]);
  const canEditLayout = !narrow;

  const gridLayout: Layout[] = useMemo(() => shownLayout.map((item) => {
    const definition = getDashboardWidgetDefinition(item.id);
    return {
      i: item.id,
      x: narrow ? 0 : item.x,
      y: item.y,
      w: narrow ? 1 : item.w,
      h: item.h,
      minW: narrow ? 1 : definition.minW,
      maxW: narrow ? 1 : definition.maxW,
      minH: definition.minH,
      isDraggable: editing && !narrow,
      isResizable: editing && !narrow,
    };
  }), [shownLayout, narrow, editing]);

  const loadDashboardData = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(false);
    try {
      setData(await fetchDashboardDataFromApi());
      setLastRefreshedAt(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setRefreshError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!isTester) loadDashboardData();
    else setLoading(false);
  }, [loadDashboardData, isTester]);

  // Consume the one-time redirect marker while retaining the alert in local
  // component state. Returning to the dashboard later must not re-show an old
  // access-denied notice from browser history.
  useEffect(() => {
    if (accessDeniedState) {
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  }, [accessDeniedState, location.pathname, location.search, navigate]);

  usePageAutoRefresh(loadDashboardData, {
    enabled: !isTester,
    intervalMs: 60_000,
  });

  const saveLayout = (layout: DashboardWidgetLayoutItem[]): boolean => {
    // The default layout is stored as null so later catalog changes reach the user.
    const toStore = isDefaultDashboardLayout(layout, dashboardAccess) ? null : layout;
    try {
      writeDashboardLayout(user?.id, toStore);
      setStoredLayout(toStore);
      feedback.success(t('dashboard:customize.saved'));
      return true;
    } catch (error) {
      console.error('Failed to save dashboard layout:', error);
      feedback.error(t('dashboard:customize.saveFailed'));
      return false;
    }
  };

  const startEditing = () => {
    setDraft(pinnedWidgets);
    setEditing(true);
  };

  const cancelEditing = () => setEditing(false);

  const finishEditing = () => {
    if (saveLayout(draft)) setEditing(false);
  };

  // Adding always goes through edit mode, so the new widget is placed and
  // saved with „Zapisz układ” like any other change.
  const addWidget = (id: DashboardWidgetId) => {
    const base = editing ? draft : pinnedWidgets;
    const definition = getDashboardWidgetDefinition(id);
    const bottom = base.reduce((max, item) => Math.max(max, item.y + item.h), 0);
    const next = [...base, { id, x: 0, y: bottom, w: definition.defaultW, h: definition.defaultH }];
    if (!canEditLayout) {
      // No grid editing on narrow screens: the widget is added and saved at once.
      saveLayout(next);
      return;
    }
    setDraft(next);
    setEditing(true);
  };

  const removeWidget = (id: DashboardWidgetId) => {
    setDraft((current) => current.filter((item) => item.id !== id));
  };

  const onGridLayoutChange = (next: Layout[]) => {
    if (!editing || narrow) return;
    setDraft(next.map((item) => ({ id: item.i as DashboardWidgetId, x: item.x, y: item.y, w: item.w, h: item.h })));
  };

  const renderStatus = (status: string) => (
    <Tag color={ORDER_STATUS_COLORS[status] || 'default'}>
      {t(`orders:statusOptions.${status}`, { defaultValue: t('common:unknownStatus') })}
    </Tag>
  );

  const orderLink = (order: OrderDto) => (
    <Link to={`/app/orders/${order.id}`}>{order.displayOrderNumber || order.orderNumber}</Link>
  );

  const bucketIcon = (bucket: Pick<DashboardBucketViewModel, 'icon'>) =>
    (bucket.icon && BUCKET_ICONS[bucket.icon]) || <AlertOutlined />;

  const dashboardModel = useMemo(() => {
    const operationalBuckets = buildDashboardBucketViewModels(data?.operationalBuckets || [], 'operational');
    const sideBuckets = buildDashboardBucketViewModels(data?.sideBuckets || [], 'side');
    const deadlineBucket = buildDashboardDeadlineBucket(data?.upcomingDeadlineCount || 0);
    const statusCards = buildDashboardStatusCards(data?.statusCounts || {}, operationalBuckets, sideBuckets, data?.overdueCount || 0);
    const findBucket = (key: string) => operationalBuckets.find((bucket) => bucket.key === key);
    const findSideBucket = (key: string) => sideBuckets.find((bucket) => bucket.key === key);
    const attention = findBucket('attention');
    const dispatch = findBucket('dispatch');
    const transport = findBucket('transport');
    const production = findSideBucket('production');

    return {
      operationalBuckets,
      sideBuckets,
      deadlineBucket,
      statusCards,
      attention,
      dispatch,
      transport,
      production,
    };
  }, [data]);

  const monthlyRevenue = data?.stats.monthlyRevenue || 0;
  const monthlyProfit = data?.stats.monthlyProfit === null ? null : data?.stats.monthlyProfit || 0;
  const averageMargin = monthlyProfit == null ? null : monthlyRevenue > 0 ? (monthlyProfit / monthlyRevenue) * 100 : 0;
  const customerPayments = data?.customerPayments;
  const upcomingDeadlines = data?.upcomingDeadlines || [];
  const deadlineCountsByDate = new Map((data?.upcomingDeadlineDays || []).map((day) => [day.date, day.count]));
  const weekDays = Array.from({ length: 7 }, (_, index) => {
    const date = dayjs().add(index, 'day');
    const key = date.format('YYYY-MM-DD');
    const count = deadlineCountsByDate.get(key) || 0;

    return {
      key,
      date,
      count,
      label: index === 0 ? t('dashboard:week.today') : date.format('DD.MM'),
    };
  });

  const priorityItems = [
    {
      key: 'overdue',
      label: t('dashboard:priorities.overdue'),
      meta: t('dashboard:risks.oldest', { count: data?.overdueCount || 0 }),
      value: data?.overdueCount || 0,
      icon: <ExclamationCircleOutlined />,
      href: '/app/orders?deadline=overdue',
      tone: 'danger',
    },
    {
      key: 'documents',
      label: t('dashboard:priorities.documents'),
      meta: t('dashboard:kpi.documentsMeta'),
      value: dashboardModel.dispatch?.count || 0,
      icon: <FileDoneOutlined />,
      href: dashboardModel.dispatch ? buildDashboardBucketLink(dashboardModel.dispatch) : '/app/orders',
      tone: 'warning',
    },
    {
      key: 'transport',
      label: t('dashboard:priorities.transport'),
      meta: t('dashboard:kpi.inTransportMeta'),
      value: dashboardModel.transport?.count || 0,
      icon: <TruckOutlined />,
      href: dashboardModel.transport ? buildDashboardBucketLink(dashboardModel.transport) : '/app/orders',
      tone: 'primary',
    },
    {
      key: 'production',
      label: t('dashboard:priorities.production'),
      meta: t('dashboard:risks.count', { count: dashboardModel.production?.count || 0 }),
      value: dashboardModel.production?.count || 0,
      icon: <ClockCircleOutlined />,
      href: dashboardModel.production ? buildDashboardBucketLink(dashboardModel.production) : '/app/orders',
      tone: 'neutral',
    },
  ];

  const topCards = [
    {
      key: 'total',
      label: t('dashboard:stats.totalOrders'),
      value: data?.stats.totalOrders || 0,
      icon: <ShoppingCartOutlined />,
      href: '/app/orders',
      tone: 'neutral',
      meta: t('dashboard:kpi.allOrdersMeta'),
    },
    {
      key: 'active',
      label: t('dashboard:stats.activeOrders'),
      value: data?.stats.activeOrders || 0,
      icon: <CheckSquareOutlined />,
      href: '/app/orders',
      tone: 'primary',
      meta: t('dashboard:kpi.activeOrdersMeta'),
    },
    {
      key: 'transport',
      label: t('dashboard:kpi.inTransport'),
      value: dashboardModel.transport?.count || 0,
      icon: <TruckOutlined />,
      href: dashboardModel.transport ? buildDashboardBucketLink(dashboardModel.transport) : '/app/orders',
      tone: 'primary',
      meta: t('dashboard:kpi.inTransportMeta'),
    },
    {
      key: 'deadlines',
      label: t('dashboard:workQueue.cards.deadlines'),
      value: dashboardModel.deadlineBucket.count,
      icon: <CalendarOutlined />,
      href: buildDashboardBucketLink(dashboardModel.deadlineBucket),
      tone: 'warning',
      meta: t('dashboard:kpi.deadlinesMeta'),
    },
    {
      key: 'documents',
      label: t('dashboard:kpi.documentsToIssue'),
      value: dashboardModel.dispatch?.count || 0,
      icon: <FileTextOutlined />,
      href: dashboardModel.dispatch ? buildDashboardBucketLink(dashboardModel.dispatch) : '/app/orders',
      tone: 'warning',
      meta: t('dashboard:kpi.documentsMeta'),
    },
    {
      key: 'overdue',
      label: t('dashboard:kpi.overdueTasks'),
      value: data?.overdueCount || 0,
      icon: <WarningOutlined />,
      href: '/app/orders?deadline=overdue',
      tone: 'danger',
      meta: t('dashboard:kpi.overdueMeta'),
    },
  ];

  // Shortcuts to pages a role cannot open would only bounce to access denied.
  // Each gate is the same predicate its route uses.
  const canOpenWarehouse = authService.canViewWarehouseDocuments();
  const quickActions = [
    { key: 'newOrder', href: '/app/orders/new', icon: <ShoppingCartOutlined />, label: t('dashboard:customize.quick.newOrder'), visible: true },
    { key: 'customers', href: '/app/customers', icon: <UserOutlined />, label: t('dashboard:customize.quick.customers'), visible: true },
    { key: 'transports', href: '/app/transports', icon: <TruckOutlined />, label: t('dashboard:customize.quick.transports'), visible: canOpenTransports },
    { key: 'pz', href: '/app/pz', icon: <FileDoneOutlined />, label: t('dashboard:customize.quick.pz'), visible: canOpenWarehouse },
    { key: 'wz', href: '/app/wz', icon: <FileTextOutlined />, label: t('dashboard:customize.quick.wz'), visible: canOpenWarehouse },
    { key: 'tasks', href: '/app/tasks', icon: <CheckSquareOutlined />, label: t('dashboard:customize.quick.tasks'), visible: canUseTasks },
  ].filter((action) => action.visible);

  const queueColumns: ColumnsType<OrderDto> = [
    {
      title: t('dashboard:recentOrders.orderNumber'),
      key: 'orderNumber',
      render: (_, order) => orderLink(order),
    },
    {
      title: t('dashboard:recentOrders.customer'),
      key: 'customer',
      render: (_, order) => order.customer?.id ? (
        <Link to={`/app/customers/${order.customer.id}`}>{order.customerName || order.customer.name}</Link>
      ) : (
        order.customerName || '-'
      ),
    },
    {
      title: t('dashboard:recentOrders.status'),
      dataIndex: 'status',
      key: 'status',
      render: renderStatus,
    },
    ...(canViewFullKpis ? [
      {
        title: t('dashboard:recentOrders.revenue'),
        dataIndex: 'revenue',
        key: 'revenue',
        render: (value: number) => value ? formatCurrency(value, 'PLN') : '-',
      },
      {
        title: t('dashboard:recentOrders.margin'),
        dataIndex: 'marginPercent',
        key: 'marginPercent',
        // One cost rule (orderMarginSummary): an unknown or incomplete cost
        // never reads as a percent — „100%” was the revenue without its cost.
        render: (value: number | null, order: OrderDto) => {
          const costState = getOrderMarginCostState(order);
          if (costState !== 'KNOWN') {
            return (
              <span className="dashboard-value-warning" data-testid="dashboard-margin-cost-state" data-cost-state={costState}>
                {t(costState === 'UNKNOWN' ? 'dashboard:recentOrders.costUnknown' : 'dashboard:recentOrders.costIncomplete')}
              </span>
            );
          }
          return value != null ? (
            <span className={value < 15 ? 'dashboard-value-danger' : 'dashboard-value-success'}>
              {formatLocalizedPercent(value, i18n.language)}
            </span>
          ) : '-';
        },
      },
    ] : []),
    {
      title: t('dashboard:table.action'),
      key: 'action',
      render: (_, order) => (
        <Link className="dashboard-icon-link" to={`/app/orders/${order.id}`} aria-label={t('dashboard:table.openOrder')}>
          <EyeOutlined />
        </Link>
      ),
    },
  ];

  if (loading) {
    return <Spin size="large" className="dashboard-loading" />;
  }

  if (isTester) {
    return (
      <div className="app-page dashboard-page">
        {accessDenied && (
          <Alert
            message={t('common:accessDeniedMessage')}
            type="warning"
            showIcon
            closable
            onClose={() => setAccessDenied(false)}
          />
        )}
        <section className="dashboard-section dashboard-message">
          <DashboardWidgetHeader
            title={t('dashboard:welcome', { name: user?.name || 'User' })}
            icon={<UserOutlined />}
            tone="blue"
          />
          <div className="dashboard-message__body">
            <Text type="secondary">{t('dashboard:tester.message')}</Text>
            <div className="dashboard-message__actions">
              <Button href="/app/customers">{t('dashboard:tester.customers')}</Button>
              <Button type="primary" href="/app/orders">{t('dashboard:tester.createOrder')}</Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="app-page dashboard-page">
        <Alert
          message={t('dashboard:error.loadingFailed')}
          type="error"
          showIcon
          action={(
            <Button size="small" icon={<ReloadOutlined />} loading={refreshing} onClick={loadDashboardData}>
              {t('common:refresh')}
            </Button>
          )}
        />
      </div>
    );
  }

  // A count with its label and explanation, the whole row opening the list it
  // counts. Zero reads muted so the rows that need work stand out.
  const renderCountRow = (row: {
    key: string;
    href: string;
    label: string;
    meta: string;
    value: number;
    icon?: React.ReactNode;
    tone?: string;
  }) => (
    <Link
      key={row.key}
      to={row.href}
      className={`dashboard-count-row dashboard-count-row--${row.tone || 'neutral'}${row.value === 0 ? ' is-zero' : ''}`}
    >
      {row.icon && (
        <ObjectCardTile tone={row.value === 0 ? 'gray' : COUNT_TONE_TILES[row.tone || 'neutral'] || 'gray'} size="sm">
          {row.icon}
        </ObjectCardTile>
      )}
      <span className="dashboard-count-row__body">
        <span className="dashboard-count-row__label" title={row.label}>{row.label}</span>
        <span className="dashboard-count-row__meta" title={row.meta}>{row.meta}</span>
      </span>
      <span className="dashboard-count-row__value">{formatLocalizedQuantity(row.value, i18n.language, 0)}</span>
      <RightOutlined className="dashboard-count-row__chevron" />
    </Link>
  );

  // One renderer per catalog id (dashboardWidgets.ts). The page only decides
  // placement; the data each widget shows is already role-redacted.
  const renderWidget = (id: DashboardWidgetId): React.ReactNode => {
    switch (id) {
      case 'kpi':
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={t(getDashboardWidgetDefinition(id).labelKey)}
              icon={<DashboardOutlined />}
              tone="blue"
              aside={(
                <Link to="/app/orders">{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>
              )}
            />
            <div className="dashboard-count-grid dashboard-count-grid--three">
              {topCards.map((card) => renderCountRow(card))}
            </div>
          </section>
        );

      case 'payments':
        return customerPayments ? <CustomerPaymentsPanel customerPayments={customerPayments} /> : null;

      case 'finance':
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={t('dashboard:financial.title')}
              icon={<LineChartOutlined />}
              tone="green"
              aside={(
                <Link to="/app/reports">{t('dashboard:financial.reports')} <ArrowRightOutlined /></Link>
              )}
            />
            <div className="dashboard-metric-grid">
              <div className="dashboard-metric">
                <span className="dashboard-metric__label">{t('dashboard:stats.monthlyRevenue')}</span>
                <strong className="dashboard-metric__value">{formatCurrency(monthlyRevenue, 'PLN')}</strong>
                <span className="dashboard-metric__meta">{t('dashboard:finance.monthScope')}</span>
              </div>
              <div className="dashboard-metric">
                <span className="dashboard-metric__label">{t('dashboard:stats.monthlyProfit')}</span>
                <strong className={`dashboard-metric__value${monthlyProfit == null ? ' is-pending' : ''}`}>{formatCurrency(monthlyProfit, 'PLN')}</strong>
                <span className="dashboard-metric__meta">{t('dashboard:finance.monthScope')}</span>
              </div>
              <div className="dashboard-metric">
                <span className="dashboard-metric__label">{t('dashboard:finance.averageMargin')}</span>
                <strong className={`dashboard-metric__value${averageMargin == null ? ' is-pending' : ''}`}>
                  {averageMargin == null ? pendingInvoiceCostLabel : formatLocalizedPercent(averageMargin, i18n.language, 2)}
                </strong>
                <span className="dashboard-metric__meta">{t('dashboard:finance.calculatedFromVisibleMonth')}</span>
              </div>
            </div>
          </section>
        );

      case 'priorities':
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={t('dashboard:priorities.title')}
              icon={<FlagOutlined />}
              tone="orange"
              aside={(
                <Link to="/app/tasks">{t('dashboard:priorities.openTasks')} <ArrowRightOutlined /></Link>
              )}
            />
            <div className="dashboard-count-grid">
              {priorityItems.map((item) => renderCountRow(item))}
            </div>
          </section>
        );

      case 'week':
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={t('dashboard:week.title')}
              icon={<CalendarOutlined />}
              tone="blue"
              aside={(
                <Text type="secondary">{t('dashboard:week.total', { count: dashboardModel.deadlineBucket.count })}</Text>
              )}
            />
            <div className="dashboard-week-strip">
              {weekDays.map((day) => (
                <Link key={day.key} to="/app/orders?deadline=upcoming" className={day.count > 0 ? 'dashboard-week-day dashboard-week-day--active' : 'dashboard-week-day'}>
                  <span className="dashboard-week-day__label">{day.label}</span>
                  <strong className="dashboard-week-day__count">{day.count}</strong>
                </Link>
              ))}
            </div>
            <div className="dashboard-week-next">
              <CalendarOutlined />
              <span className="dashboard-week-next__body">
                <span className="dashboard-week-next__label">{t('dashboard:week.next')}</span>
                <span className="dashboard-week-next__date">
                  {upcomingDeadlines[0]?.estimatedCompletionDate ? dayjs(upcomingDeadlines[0].estimatedCompletionDate).format('YYYY-MM-DD') : '-'}
                </span>
              </span>
              <Link to={buildDashboardBucketLink(dashboardModel.deadlineBucket)}>{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>
            </div>
          </section>
        );

      case 'status':
        return (
          <section className="dashboard-section dashboard-status-section">
            <DashboardWidgetHeader
              title={t('dashboard:statusCards.title')}
              icon={<PieChartOutlined />}
              tone="teal"
              aside={(
                <Link to="/app/orders">{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>
              )}
            />
            <div className="dashboard-status-grid">
              {dashboardModel.statusCards.map((card) => (
                <Link
                  key={card.key}
                  to={card.href}
                  className={`dashboard-status-card dashboard-status-card--${card.tone}${card.value === 0 ? ' is-zero' : ''}`}
                >
                  <span className="dashboard-status-card__label">{t(card.labelKey)}</span>
                  <strong className="dashboard-status-card__value">{formatLocalizedQuantity(card.value, i18n.language, 0)}</strong>
                </Link>
              ))}
            </div>
          </section>
        );

      case 'risks':
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={canViewFullKpis ? t('dashboard:risks.combinedTitle') : t('dashboard:sidePanel.title')}
              icon={<AlertOutlined />}
              tone="pink"
              aside={canViewFullKpis && <Link to="/app/reports">{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>}
            />
            <div className="dashboard-count-grid dashboard-count-grid--one">
              {renderCountRow({
                key: 'overdue',
                href: '/app/orders?deadline=overdue',
                label: t('dashboard:sidePanel.overdue'),
                meta: t('dashboard:risks.oldest', { count: data.overdueCount }),
                value: data.overdueCount,
                icon: <ExclamationCircleOutlined />,
                tone: 'danger',
              })}
              {dashboardModel.sideBuckets.slice(0, 2).map((bucket) => renderCountRow({
                key: bucket.key,
                href: buildDashboardBucketLink(bucket),
                label: t(bucket.labelKey),
                meta: t('dashboard:risks.count', { count: bucket.count }),
                value: bucket.count,
                icon: bucketIcon(bucket),
              }))}
            </div>

            {canViewFullKpis && (
              <div className="dashboard-margin-list">
                <div className="dashboard-eyebrow">
                  <span>{t('dashboard:lowMarginOrders.title')}</span>
                  <span>{t('dashboard:risks.marginHint')}</span>
                </div>
                {(data.lowMarginOrders || []).length > 0 ? (
                  (data.lowMarginOrders || []).slice(0, 3).map((order) => (
                    <Link key={order.id} to={`/app/orders/${order.id}`} className="dashboard-list-row">
                      <span className="dashboard-list-row__main">
                        <span className="dashboard-list-row__title">{order.displayOrderNumber || order.orderNumber}</span>
                        <span className="dashboard-list-row__meta" title={order.customerName || undefined}>{order.customerName || '-'}</span>
                      </span>
                      <Tag color="red">{formatLocalizedPercent(order.marginPercent || 0, i18n.language)}</Tag>
                    </Link>
                  ))
                ) : (
                  <Text type="secondary" className="dashboard-empty-line">{t('dashboard:lowMarginOrders.noOrders')}</Text>
                )}
              </div>
            )}
          </section>
        );

      case 'recent':
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={t('dashboard:recentOrders.title')}
              icon={<ShoppingCartOutlined />}
              tone="blue"
              aside={(
                <Link to="/app/orders">{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>
              )}
            />
            <Table
              className="dashboard-table"
              dataSource={data.recentOrders.slice(0, 5)}
              columns={queueColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </section>
        );

      case 'workQueue':
        // Each order with the step it waits for, so the panel says what to do next.
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={t(getDashboardWidgetDefinition(id).labelKey)}
              icon={<UnorderedListOutlined />}
              tone="indigo"
              aside={(
                <Link to="/app/orders">{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>
              )}
            />
            <div className="dashboard-list">
              {data.workQueue.slice(0, 6).map((order) => (
                <div key={order.id} className="dashboard-list-row">
                  <span className="dashboard-list-row__main">
                    <span className="dashboard-list-row__title">
                      {orderLink(order)}
                      <span className="dashboard-list-row__party" title={order.customerName || order.customer?.name || ''}>
                        {order.customerName || order.customer?.name || '-'}
                      </span>
                    </span>
                    <span className="dashboard-list-row__next">
                      {t(`dashboard:workQueue.nextActions.${order.status}`, { defaultValue: t('dashboard:workQueue.nextActions.default') })}
                    </span>
                  </span>
                  {renderStatus(order.status)}
                </div>
              ))}
              {data.workQueue.length === 0 && (
                <Text type="secondary" className="dashboard-empty-line">{t('dashboard:workQueue.empty')}</Text>
              )}
            </div>
          </section>
        );

      case 'upcoming':
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={t(getDashboardWidgetDefinition(id).labelKey)}
              icon={<ClockCircleOutlined />}
              tone="blue"
              aside={(
                <Link to="/app/orders?deadline=upcoming">{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>
              )}
            />
            <div className="dashboard-list">
              {upcomingDeadlines.slice(0, 6).map((order) => (
                <div key={order.id} className="dashboard-list-row">
                  <span className="dashboard-list-row__main">
                    {orderLink(order)}
                    <span className="dashboard-list-row__meta" title={order.customerName || order.customer?.name || undefined}>{order.customerName || order.customer?.name || '-'}</span>
                  </span>
                  <span className="dashboard-list-row__date">
                    {order.estimatedCompletionDate ? dayjs(order.estimatedCompletionDate).format('DD.MM') : '-'}
                  </span>
                </div>
              ))}
              {upcomingDeadlines.length === 0 && (
                <Text type="secondary" className="dashboard-empty-line">{t('dashboard:customize.emptyList')}</Text>
              )}
            </div>
          </section>
        );

      case 'statusChart': {
        const rows = Object.entries(data.statusCounts || {})
          .filter(([, count]) => count > 0)
          .sort(([, a], [, b]) => b - a);
        const maxCount = rows[0]?.[1] || 1;
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={t(getDashboardWidgetDefinition(id).labelKey)}
              icon={<BarChartOutlined />}
              tone="teal"
            />
            <div className="dashboard-list">
              {rows.map(([status, count]) => (
                <div key={status} className="dashboard-bar-row">
                  <span className="dashboard-bar-row__label">{t(`orders:statusOptions.${status}`, { defaultValue: t('common:unknownStatus') })}</span>
                  <Progress percent={(count / maxCount) * 100} showInfo={false} size="small" className="dashboard-bar" />
                  <span className="dashboard-bar-row__value">{formatLocalizedQuantity(count, i18n.language, 0)}</span>
                </div>
              ))}
              {rows.length === 0 && (
                <Text type="secondary" className="dashboard-empty-line">{t('dashboard:customize.emptyList')}</Text>
              )}
            </div>
          </section>
        );
      }

      case 'upcomingTransports':
        return <UpcomingTransportsWidget refreshKey={refreshTick} />;

      case 'expectedDeliveries':
        return <ExpectedDeliveriesWidget refreshKey={refreshTick} />;

      case 'myTasks':
        return <MyTasksWidget refreshKey={refreshTick} />;

      case 'quickActions':
        return (
          <section className="dashboard-section">
            <DashboardWidgetHeader
              title={t(getDashboardWidgetDefinition(id).labelKey)}
              icon={<ThunderboltOutlined />}
              tone="orange"
            />
            <div className="dashboard-quick-grid">
              {quickActions.map((action) => (
                <Link key={action.key} to={action.href} className="dashboard-quick-action">
                  {action.icon}
                  <span>{action.label}</span>
                </Link>
              ))}
            </div>
          </section>
        );

      default:
        return null;
    }
  };

  return (
    <div className="app-page dashboard-page">
      {accessDenied && (
        <Alert
          message={t('common:accessDeniedMessage')}
          type="warning"
          showIcon
          closable
          onClose={() => setAccessDenied(false)}
        />
      )}
      {refreshError && (
        <Alert
          message={t('dashboard:error.loadingFailed')}
          description={t('dashboard:error.showingPreviousData')}
          type="warning"
          showIcon
          closable
          action={(
            <Button size="small" icon={<ReloadOutlined />} loading={refreshing} onClick={loadDashboardData}>
              {t('common:refresh')}
            </Button>
          )}
        />
      )}

      {/* One quiet row: when the data was read on the left, the panel's own
          controls on the right. The widgets carry the content. */}
      <div className="dashboard-toolbar">
        {editing ? (
          <Text type="secondary" className="dashboard-toolbar__hint">{t('dashboard:customize.editHint')}</Text>
        ) : (
          <div className="dashboard-toolbar__refresh">
            <Text type="secondary">
              {t('dashboard:lastRefresh', {
                date: lastRefreshedAt ? dayjs(lastRefreshedAt).format('YYYY-MM-DD HH:mm:ss') : '-',
              })}
            </Text>
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              loading={refreshing}
              onClick={loadDashboardData}
            >
              {t('common:refresh')}
            </Button>
          </div>
        )}
        <div className="dashboard-toolbar__actions">
          {editing ? (
            <>
              <Button icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                {t('dashboard:customize.addWidget')}
              </Button>
              <Button icon={<UndoOutlined />} onClick={() => setDraft(getDefaultDashboardLayout(dashboardAccess))}>
                {t('dashboard:customize.reset')}
              </Button>
              <Button icon={<ClearOutlined />} onClick={() => setDraft([])} disabled={draft.length === 0}>
                {t('dashboard:customize.clear')}
              </Button>
              <Button onClick={cancelEditing}>{t('common:cancel')}</Button>
              <Button type="primary" icon={<CheckOutlined />} onClick={finishEditing}>
                {t('dashboard:customize.done')}
              </Button>
            </>
          ) : (
            <>
              <Button icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                {t('dashboard:customize.addWidget')}
              </Button>
              {!narrow && (
                <Button icon={<EditOutlined />} onClick={startEditing}>
                  {t('dashboard:customize.edit')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {!editing && pinnedWidgets.length === 0 ? (
        <section className="dashboard-section dashboard-message">
          <div className="dashboard-message__body">
            <Text type="secondary">{t('dashboard:customize.emptyPanel')}</Text>
            <div className="dashboard-message__actions">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                {t('dashboard:customize.addWidget')}
              </Button>
            </div>
          </div>
        </section>
      ) : narrow ? (
        // Phone and tablet widths: widgets stack at their natural height.
        // Fixed grid heights would box long content into small scroll areas.
        <div className="dashboard-stack">
          {shownLayout.map((item) => (
            <div key={item.id} className="dashboard-stack__item" data-widget-id={item.id}>
              {renderWidget(item.id)}
            </div>
          ))}
        </div>
      ) : (
        <ResponsiveGridLayout
          className={`dashboard-grid${editing ? ' dashboard-grid--editing' : ''}`}
          layout={gridLayout}
          cols={narrow ? 1 : DASHBOARD_GRID_COLUMNS}
          rowHeight={GRID_ROW_HEIGHT_PX}
          margin={[GRID_GAP_PX, GRID_GAP_PX]}
          containerPadding={[0, 0]}
          compactType="vertical"
          isDraggable={editing && canEditLayout}
          isResizable={editing && canEditLayout}
          draggableCancel=".dashboard-grid-item__remove"
          resizeHandles={['se']}
          onLayoutChange={onGridLayoutChange}
        >
          {shownLayout.map((item) => (
            <div key={item.id} className="dashboard-grid-item" data-widget-id={item.id}>
              {renderWidget(item.id)}
              {editing && (
                <Button
                  className="dashboard-grid-item__remove"
                  size="small"
                  shape="circle"
                  icon={<CloseOutlined />}
                  aria-label={t('dashboard:customize.remove')}
                  onClick={() => removeWidget(item.id)}
                />
              )}
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      <DashboardAddWidgetModal
        open={addOpen}
        widgets={availableWidgets}
        layout={shownLayout}
        onClose={() => setAddOpen(false)}
        onAdd={addWidget}
      />
    </div>
  );
};

export default Dashboard;
